import React from 'react';
import { Modal } from 'react-bootstrap';
import { Warning48Filled } from '@fluentui/react-icons';
import styles from './DoubleConfirmationModal.module.css';

/**
 * Recalculate Schedule?
 * Step 1: Update only this task vs Recalculate downstream
 * Step 2 (after downstream): Keep vs adjust downstream deadlines
 */
const RecalculateScheduleModal = ({
    open,
    onClose,
    onUpdateOnly,
    onRecalculateDownstream,
    fieldLabel = 'Duration',
    affectedCount,
    confirmLoading = false,
}) => {
    const [step, setStep] = React.useState('schedule');

    React.useEffect(() => {
        if (!open) setStep('schedule');
    }, [open]);

    const normalizedLabel = String(fieldLabel || '').toLowerCase();
    const isDeadlineStep = step === 'deadlines';
    const isDeleteFlow = normalizedLabel === 'delete';
    const isInsertFlow = normalizedLabel === 'insert';
    const isReorderFlow = normalizedLabel === 'task order';
    const isModeFlow = normalizedLabel === 'mode';
    const isTimelineFlow = normalizedLabel === 'timeline';
    const isDurationFlow = normalizedLabel === 'duration';
    const isStatusFlow = normalizedLabel === 'status';
    const isDeadlineFlow = normalizedLabel === 'deadline';
    const skipDeadlineStep = isDeleteFlow || isReorderFlow || isInsertFlow;

    const title = isDeadlineStep
        ? 'Recalculate deadlines?'
        : isDeleteFlow
          ? 'Delete task?'
          : isInsertFlow
            ? 'Recalculate following schedule?'
            : isReorderFlow
              ? 'Recalculate schedule after reorder?'
              : 'Recalculate Schedule?';

    const message = (() => {
        if (isDeadlineStep) {
            return 'Following task timelines will change. Would you like each deadline set to that task\'s new planned End Date?';
        }
        if (isDeleteFlow) {
            return 'Deleting this task changes the scheduling relationship for following tasks. Would you like to keep their current timelines or recalculate the following schedule after deletion?';
        }
        if (isInsertFlow) {
            return 'Adding this task here changes the scheduling relationship for following tasks. Would you like to keep their existing timelines or recalculate the following schedule?';
        }
        if (isReorderFlow) {
            return 'Changing task order changes scheduling relationships for affected tasks. Would you like to keep existing timelines or recalculate the schedule using the new order?';
        }
        if (isModeFlow) {
            return 'Changing the Mode of this task can affect the schedule of following tasks. Would you like to update only this task or recalculate the following schedule?';
        }
        if (isTimelineFlow) {
            return 'You changed the Timeline of this task. This can affect the schedule of following tasks. Would you like to update only this task or recalculate the following schedule?';
        }
        if (isDurationFlow) {
            return 'You changed the Duration of this task. This can affect the schedule of following tasks. Would you like to update only this task or recalculate the following schedule?';
        }
        if (isStatusFlow) {
            return 'This status change changes the scheduling anchor for following tasks. Would you like to keep their current timelines or recalculate the following schedule?';
        }
        if (isDeadlineFlow) {
            return 'Adjusting this task\'s Timeline can affect the schedule of following tasks. Would you like to keep their existing timelines or recalculate the following schedule?';
        }
        return 'You changed the schedule of this task. Would you like to update only this task or recalculate the following schedule?';
    })();

    const updateOnlyLabel = (() => {
        if (isDeleteFlow) return 'Delete only — keep timelines';
        if (isInsertFlow || isReorderFlow || isDeadlineFlow) return 'Keep following timelines unchanged';
        return 'Update only this task';
    })();

    const recalculateLabel = (() => {
        if (isDeleteFlow) return 'Delete and recalculate';
        if (isInsertFlow || isReorderFlow || isDeadlineFlow) return 'Recalculate downstream';
        return 'Recalculate downstream';
    })();

    const runIfIdle = action => {
        if (!confirmLoading) action();
    };

    const linkStyle = {
        opacity: confirmLoading ? 0.6 : 1,
        pointerEvents: confirmLoading ? 'none' : 'auto',
        textAlign: 'center',
    };

    const handleClose = () => {
        if (confirmLoading) return;
        setStep('schedule');
        onClose();
    };

    return (
        <Modal centered show={open} onHide={handleClose} className={styles.modalMainBody}>
            <Modal.Body className={styles.confirmationModalBody}>
                <div className={styles.confirmationModalMsg}>
                    <div className="mb-4 text-center">
                        <Warning48Filled color="var(--Brand-Primary)" />
                    </div>
                    <h3 style={{ textAlign: 'center', fontSize: 20, fontWeight: 600, marginBottom: 16 }}>{title}</h3>
                    <p className={styles.message} style={{ marginBottom: 34 }}>
                        {message}
                    </p>
                    {!isDeadlineStep && affectedCount ? (
                        <p className={styles.message} style={{ marginBottom: 24, fontSize: 13, color: '#5A5F7A' }}>
                            {affectedCount} following task{affectedCount === 1 ? '' : 's'} will be affected.
                        </p>
                    ) : null}
                    {isDeadlineStep ? (
                        <p className={styles.message} style={{ marginBottom: 24, fontSize: 13, color: '#5A5F7A' }}>
                            Each affected deadline will be set to that task&apos;s planned End Date.
                            {affectedCount ? ` ${affectedCount} deadline${affectedCount === 1 ? '' : 's'} will be affected.` : ''}
                        </p>
                    ) : null}
                    <div className="d-flex flex-column gap-3">
                        {isDeadlineStep ? (
                            <>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => runIfIdle(() => onRecalculateDownstream({ adjustDeadlines: false }))}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            runIfIdle(() => onRecalculateDownstream({ adjustDeadlines: false }));
                                        }
                                    }}
                                    className={styles.secondaryLink}
                                    style={linkStyle}>
                                    Keep deadlines unchanged
                                </span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => runIfIdle(() => onRecalculateDownstream({ adjustDeadlines: true }))}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            runIfIdle(() => onRecalculateDownstream({ adjustDeadlines: true }));
                                        }
                                    }}
                                    className={styles.orangeLink}
                                    style={linkStyle}>
                                    Adjust deadlines
                                </span>
                            </>
                        ) : (
                            <>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => runIfIdle(onUpdateOnly)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') runIfIdle(onUpdateOnly);
                                    }}
                                    className={isDeleteFlow ? styles.secondaryLink : styles.orangeLink}
                                    style={linkStyle}>
                                    {updateOnlyLabel}
                                </span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        runIfIdle(() =>
                                            skipDeadlineStep
                                                ? onRecalculateDownstream({ adjustDeadlines: false })
                                                : setStep('deadlines'),
                                        )
                                    }
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            runIfIdle(() =>
                                                skipDeadlineStep
                                                    ? onRecalculateDownstream({ adjustDeadlines: false })
                                                    : setStep('deadlines'),
                                            );
                                        }
                                    }}
                                    className={styles.orangeLink}
                                    style={linkStyle}>
                                    {recalculateLabel}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default RecalculateScheduleModal;
